import { useMemo, useState, type JSX } from "react";
import { useNavigate, useParams, Link } from "react-router";
import { useLiveQuery } from "dexie-react-hooks";
import { toast } from "sonner";
import { ChevronLeft, Pencil, Trash2, BookOpen } from "lucide-react";
import {
  SHOPPING_CATEGORIES,
  type ShoppingCategory,
  type ShoppingListApiItem,
  type ShoppingListItem,
} from "@recipevault/shared";
import { db } from "../../sync/db.js";
import { useSync } from "../../sync/useSync.js";
import { useLongPress } from "../../shared/useLongPress.js";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/ui/sheet.js";
import { AddItemRow } from "./AddItemRow.js";
import { EditItemSheet } from "./EditItemSheet.js";
import { CATEGORY_LABELS } from "./categoryLabels.js";

interface ItemGroup {
  category: ShoppingCategory;
  label: string;
  items: ShoppingListItem[];
}

/** "2 kg" style prefix from qty + unit; empty string when neither is set. */
function quantityPrefix(item: ShoppingListItem): string {
  const parts: string[] = [];
  if (item.qty != null) parts.push(String(item.qty));
  if (item.unit) parts.push(item.unit);
  return parts.join(" ");
}

/**
 * Store-mode row: a single big tap target. Short tap toggles checked state;
 * long-press (Task 14) opens the EditItemSheet for this item instead — the
 * two are coordinated via useLongPress's wrapTap so a long-press never also
 * fires the toggle. Used both for unchecked rows in a category group and for
 * checked rows inside the "In cart" accordion (where a tap un-checks the
 * item and it returns to its group).
 */
function StoreModeRow({
  item,
  onToggle,
  onEdit,
}: {
  item: ShoppingListItem;
  onToggle: (item: ShoppingListItem) => void;
  onEdit: (item: ShoppingListItem) => void;
}): JSX.Element {
  const longPress = useLongPress(() => {
    onEdit(item);
  });

  return (
    <li>
      <button
        type="button"
        aria-pressed={item.checked}
        aria-label={`Toggle ${item.name}`}
        onPointerDown={longPress.onPointerDown}
        onPointerMove={longPress.onPointerMove}
        onPointerUp={longPress.onPointerUp}
        onPointerLeave={longPress.onPointerLeave}
        onPointerCancel={longPress.onPointerCancel}
        onClick={longPress.wrapTap(() => void onToggle(item))}
        className="flex min-h-14 w-full items-center gap-3 px-3 py-2 text-left"
      >
        <span
          aria-hidden
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border-2 transition-colors ${
            item.checked
              ? "border-emerald-500 bg-emerald-500 text-white"
              : "border-zinc-500 bg-transparent"
          }`}
        >
          {item.checked && (
            <svg viewBox="0 0 12 12" fill="none" className="h-3.5 w-3.5" aria-hidden>
              <path
                d="M2 6l3 3 5-5"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        <span className="flex min-w-0 flex-1 items-baseline gap-1.5 text-base">
          {quantityPrefix(item) && (
            <span
              className={
                item.checked ? "shrink-0 text-sm text-zinc-600" : "shrink-0 text-sm text-zinc-400"
              }
            >
              {quantityPrefix(item)}
            </span>
          )}
          <span
            className={`truncate ${item.checked ? "text-zinc-500 line-through" : "text-zinc-100"}`}
          >
            {item.name}
          </span>
        </span>
      </button>
    </li>
  );
}

export function ShoppingListDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { service } = useSync();
  const navigate = useNavigate();

  // undefined → still loading; null → resolved-not-found; object → loaded.
  const list = useLiveQuery<ShoppingListApiItem | null | undefined>(
    async () => {
      if (!id) return null;
      return (await db.shoppingLists.get(id)) ?? null;
    },
    [id],
  );

  // Exclude pending photo-import drafts from the "add from recipe" picker.
  const recipes = useLiveQuery(() => db.recipes.filter((r) => r.status !== "pending").toArray(), []);

  // Header rename
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  // Item edit — drives the EditItemSheet (Task 14 replaced the inline form).
  const [editingItem, setEditingItem] = useState<ShoppingListItem | null>(null);

  const [recipePickerOpen, setRecipePickerOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Store-mode "In cart" accordion (active lists only).
  const [inCartOpen, setInCartOpen] = useState(false);

  // ARCHIVED-list rendering only: mixed groups, checked items sunk to the
  // bottom of their category and dimmed. Kept byte-for-byte so the archived
  // read-only view is unchanged by the store-mode redesign below.
  const groups = useMemo<ItemGroup[]>(() => {
    if (!list) return [];
    const byCat = new Map<ShoppingCategory, ShoppingListItem[]>();
    for (const item of list.items) {
      const bucket = byCat.get(item.category);
      if (bucket) bucket.push(item);
      else byCat.set(item.category, [item]);
    }
    return SHOPPING_CATEGORIES.filter((c) => byCat.has(c)).map((c) => ({
      category: c,
      label: CATEGORY_LABELS[c],
      // Unchecked first, then checked (stable within each).
      items: [...(byCat.get(c) as ShoppingListItem[])].sort(
        (a, b) => Number(a.checked) - Number(b.checked),
      ),
    }));
  }, [list]);

  // ACTIVE-list "store mode": category groups hold UNCHECKED items only —
  // checked items move to the "In cart" accordion below instead.
  const storeModeGroups = useMemo<ItemGroup[]>(() => {
    if (!list) return [];
    const byCat = new Map<ShoppingCategory, ShoppingListItem[]>();
    for (const item of list.items) {
      if (item.checked) continue;
      const bucket = byCat.get(item.category);
      if (bucket) bucket.push(item);
      else byCat.set(item.category, [item]);
    }
    return SHOPPING_CATEGORIES.filter((c) => byCat.has(c)).map((c) => ({
      category: c,
      label: CATEGORY_LABELS[c],
      items: byCat.get(c) as ShoppingListItem[],
    }));
  }, [list]);

  // Per-category checked/total progress, computed across the WHOLE list (not
  // just the unchecked items in storeModeGroups) — a category header keeps
  // showing progress even once all of its own items are checked off.
  const categoryProgress = useMemo(() => {
    const map = new Map<ShoppingCategory, { checked: number; total: number }>();
    if (!list) return map;
    for (const item of list.items) {
      const entry = map.get(item.category) ?? { checked: 0, total: 0 };
      entry.total += 1;
      if (item.checked) entry.checked += 1;
      map.set(item.category, entry);
    }
    return map;
  }, [list]);

  // Checked items for the "In cart" accordion, grouped by category order.
  const checkedItems = useMemo<ShoppingListItem[]>(() => {
    if (!list) return [];
    const byCat = new Map<ShoppingCategory, ShoppingListItem[]>();
    for (const item of list.items) {
      if (!item.checked) continue;
      const bucket = byCat.get(item.category);
      if (bucket) bucket.push(item);
      else byCat.set(item.category, [item]);
    }
    return SHOPPING_CATEGORIES.flatMap((c) => byCat.get(c) ?? []);
  }, [list]);

  if (!id) {
    return <p className="text-destructive text-sm">Missing list id.</p>;
  }
  if (list === undefined) {
    return <p className="text-zinc-500 text-sm">Loading…</p>;
  }
  if (list === null) {
    return (
      <div className="py-12 text-center">
        <p className="text-zinc-300 mb-4">This list no longer exists.</p>
        <Button variant="outline" asChild>
          <Link to="/shopping">Back to lists</Link>
        </Button>
      </div>
    );
  }

  // ---- handlers (id + list are defined past here) ----
  // Re-bind as narrowed consts so the hoisted closures below see `string` /
  // `ShoppingListApiItem`, not the pre-guard `… | undefined`.
  const listId: string = id;
  const currentList: ShoppingListApiItem = list;

  function startRename(currentName: string): void {
    setNameDraft(currentName);
    setEditingName(true);
  }

  async function commitRename(): Promise<void> {
    const trimmed = nameDraft.trim();
    setEditingName(false);
    if (!trimmed || trimmed === currentList.name) return;
    try {
      await service.patchShoppingListOptimistic(listId, { name: trimmed });
    } catch {
      toast.error("Couldn't rename list — please try again");
    }
  }

  async function toggleChecked(item: ShoppingListItem): Promise<void> {
    try {
      await service.patchItemOptimistic(listId, item.id, { checked: !item.checked });
    } catch {
      toast.error("Couldn't update item — please try again");
    }
  }

  async function removeItem(itemId: string): Promise<void> {
    try {
      await service.deleteItemOptimistic(listId, itemId);
    } catch {
      toast.error("Couldn't remove item — please try again");
    }
  }

  async function addFromRecipe(recipeId: string): Promise<void> {
    try {
      await service.addItemOptimistic(listId, { fromRecipeId: recipeId });
    } catch {
      toast.error("Couldn't add recipe items — please try again");
    }
  }

  async function deleteList(): Promise<void> {
    try {
      await service.deleteShoppingListOptimistic(listId);
      void navigate("/shopping");
    } catch {
      toast.error("Couldn't delete list — please try again");
    }
  }

  async function endTrip(): Promise<void> {
    const uncheckedItems = currentList.items.filter((i) => !i.checked);
    try {
      let nextId: string | null = null;
      if (uncheckedItems.length > 0) {
        const next = await service.createShoppingListOptimistic({
          name: currentList.name,
          items: uncheckedItems.map((i) => ({
            name: i.name,
            qty: i.qty,
            unit: i.unit,
            category: i.category,
          })),
        });
        nextId = next.id;
      }
      await service.patchShoppingListOptimistic(listId, { status: "archived" });
      void navigate(nextId ? `/shopping/${nextId}` : "/shopping");
    } catch {
      toast.error("Couldn't end the trip — please try again");
    }
  }

  const isEmpty = list.items.length === 0;
  const remaining = list.items.filter((i) => !i.checked).length;
  const allChecked = !isEmpty && remaining === 0;
  const sortedRecipes = (recipes ?? [])
    .slice()
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <header className="flex flex-col gap-3">
        <Link
          to="/shopping"
          className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-200 w-fit"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          All lists
        </Link>

        <div className="flex items-start justify-between gap-3">
          {editingName ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void commitRename();
              }}
              className="flex-1"
            >
              <Input
                value={nameDraft}
                onChange={(e) => {
                  setNameDraft(e.target.value);
                }}
                onBlur={() => {
                  void commitRename();
                }}
                autoFocus
                maxLength={120}
                aria-label="List name"
                className="text-xl font-semibold"
              />
            </form>
          ) : (
            <button
              type="button"
              onClick={() => {
                startRename(list.name);
              }}
              className="group flex items-center gap-2 text-left"
              aria-label="Rename list"
            >
              <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">
                {list.name}
              </h1>
              <Pencil
                className="h-4 w-4 text-zinc-600 opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden
              />
            </button>
          )}
        </div>

        <p className="text-xs text-zinc-500">
          {isEmpty
            ? "Empty list"
            : `${remaining} of ${list.items.length} remaining`}
          {list.status === "archived" && " · archived"}
        </p>
      </header>

      {/* Items */}
      {list.status === "archived" ? (
        // Archived lists: unchanged read-only rendering (mixed groups,
        // checked items sunk to the bottom + dimmed, tap-to-edit + delete).
        isEmpty ? (
          <div className="py-10 text-center">
            <p className="text-zinc-300 font-medium mb-1">No items yet</p>
            <p className="text-zinc-500 text-sm">
              Add items below, or pull them in from a recipe.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {groups.map((group) => (
              <section key={group.category} aria-label={group.label}>
                <h2 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  {group.label}
                </h2>
                <ul className="flex flex-col divide-y divide-zinc-800/70 rounded-xl border border-zinc-800 bg-zinc-900">
                  {group.items.map((item) => (
                    <li key={item.id} className="flex items-center gap-3 px-3 py-2.5">
                      {/* Custom checkbox (IngredientsPane style) */}
                      <button
                        type="button"
                        aria-label={`Toggle ${item.name}`}
                        aria-pressed={item.checked}
                        onClick={() => void toggleChecked(item)}
                        className="shrink-0"
                      >
                        <span
                          aria-hidden
                          className={`flex h-5 w-5 items-center justify-center rounded-sm border-2 transition-colors ${
                            item.checked
                              ? "border-emerald-500 bg-emerald-500 text-white"
                              : "border-zinc-500 bg-transparent"
                          }`}
                        >
                          {item.checked && (
                            <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3" aria-hidden>
                              <path
                                d="M2 6l3 3 5-5"
                                stroke="currentColor"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </span>
                      </button>

                      {/* Name + qty — tap to edit (opens EditItemSheet, Task 14) */}
                      <button
                        type="button"
                        onClick={() => {
                          setEditingItem(item);
                        }}
                        aria-label={`Edit ${item.name}`}
                        className="flex min-w-0 flex-1 items-baseline gap-1.5 text-left text-base"
                      >
                        {quantityPrefix(item) && (
                          <span
                            className={
                              item.checked
                                ? "shrink-0 text-sm text-zinc-600"
                                : "shrink-0 text-sm text-zinc-400"
                            }
                          >
                            {quantityPrefix(item)}
                          </span>
                        )}
                        <span
                          className={`truncate ${
                            item.checked ? "text-zinc-500 line-through" : "text-zinc-100"
                          }`}
                        >
                          {item.name}
                        </span>
                      </button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Delete ${item.name}`}
                        onClick={() => void removeItem(item.id)}
                        className="shrink-0 text-zinc-500 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )
      ) : (
        // Active lists: "store mode" — big toggle rows, sticky per-category
        // progress headers, checked items collapsed into "In cart".
        <>
          <div className="flex flex-col gap-3">
            <AddItemRow listId={listId} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-fit"
              onClick={() => {
                setRecipePickerOpen(true);
              }}
            >
              <BookOpen className="mr-2 h-4 w-4" aria-hidden />
              Add a recipe
            </Button>
          </div>

          {isEmpty ? (
            <div className="py-10 text-center">
              <p className="text-zinc-300 font-medium mb-1">No items yet</p>
              <p className="text-zinc-500 text-sm">
                Add items above, or pull them in from a recipe.
              </p>
            </div>
          ) : allChecked ? (
            <div className="py-10 text-center">
              <p className="text-2xl font-semibold text-zinc-100 mb-1">All done 🎉</p>
              <p className="text-zinc-500 text-sm">
                Everything&apos;s checked off — tap End trip below to wrap up.
              </p>
            </div>
          ) : (
            <div className="max-h-[65vh] overflow-y-auto">
              <div className="flex flex-col gap-5">
                {storeModeGroups.map((group) => {
                  const progress = categoryProgress.get(group.category) ?? {
                    checked: 0,
                    total: 0,
                  };
                  return (
                    <section key={group.category} aria-label={group.label}>
                      <h2 className="sticky top-0 z-10 bg-zinc-950 py-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        {group.label} · {progress.checked}/{progress.total}
                      </h2>
                      <ul className="flex flex-col divide-y divide-zinc-800/70 rounded-xl border border-zinc-800 bg-zinc-900">
                        {group.items.map((item) => (
                          <StoreModeRow
                            key={item.id}
                            item={item}
                            onToggle={(i) => void toggleChecked(i)}
                            onEdit={setEditingItem}
                          />
                        ))}
                      </ul>
                    </section>
                  );
                })}
              </div>
            </div>
          )}

          {checkedItems.length > 0 && (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                aria-expanded={inCartOpen}
                onClick={() => {
                  setInCartOpen((v) => !v);
                }}
                className="flex min-h-12 w-full items-center justify-between gap-2 rounded-xl border border-zinc-800 bg-zinc-900 px-3 text-left"
              >
                <span className="text-sm font-semibold text-zinc-200">
                  In cart ({checkedItems.length})
                </span>
                <span className="text-xs text-zinc-500">{inCartOpen ? "Hide" : "Show"}</span>
              </button>

              {inCartOpen && (
                <ul className="flex flex-col divide-y divide-zinc-800/70 rounded-xl border border-zinc-800 bg-zinc-900">
                  {checkedItems.map((item) => (
                    <StoreModeRow
                      key={item.id}
                      item={item}
                      onToggle={(i) => void toggleChecked(i)}
                      onEdit={setEditingItem}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {/* Delete list */}
      <div className="mt-2 border-t border-zinc-800 pt-4">
        {confirmingDelete ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">Delete this list?</span>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setConfirmingDelete(false);
                void deleteList();
              }}
            >
              Delete
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setConfirmingDelete(false);
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="text-zinc-500 hover:text-destructive"
            onClick={() => {
              setConfirmingDelete(true);
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" aria-hidden />
            Delete list
          </Button>
        )}
      </div>

      {/* Recipe picker */}
      <Sheet open={recipePickerOpen} onOpenChange={setRecipePickerOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Add from a recipe</SheetTitle>
            <SheetDescription>
              Pull a recipe&apos;s ingredients onto this list.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 flex flex-col gap-1 overflow-y-auto">
            {sortedRecipes.length === 0 ? (
              <p className="text-sm text-zinc-500">No recipes yet.</p>
            ) : (
              sortedRecipes.map((recipe) => (
                <button
                  key={recipe.id}
                  type="button"
                  onClick={() => {
                    void addFromRecipe(recipe.id);
                    setRecipePickerOpen(false);
                  }}
                  className="rounded-md px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800"
                >
                  {recipe.title}
                </button>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      <EditItemSheet
        item={editingItem}
        listId={listId}
        open={editingItem !== null}
        onOpenChange={(o) => {
          if (!o) setEditingItem(null);
        }}
      />

      {/* Persistent End-trip footer (Task 14) — last in flow so `sticky bottom-0`
          naturally pins to the bottom of the scrollable content area, mirroring
          RecipeView's sticky action bar. It pins above the mobile tab bar
          (AppShell's `mb-tabbar` margin already reserves that space) and is
          padded for the iOS/Android gesture bar via `pb-safe`. Deliberately
          OUTSIDE the `max-h-[65vh]` item-scroll region above so it stays put
          while the item list scrolls underneath it. */}
      {list.status === "active" && (
        <div className="sticky bottom-0 z-30 -mx-4 md:-mx-8 mt-4 flex items-center justify-between gap-2 border-t border-zinc-800 bg-zinc-950/95 px-4 py-3 pb-safe backdrop-blur md:px-8">
          <span className="text-sm text-zinc-400">
            {remaining} item{remaining === 1 ? "" : "s"} left
          </span>
          <Button onClick={() => void endTrip()}>End trip</Button>
        </div>
      )}
    </div>
  );
}
